/**
 * Custom ChartRendererService
 * Overrides the shared library service to use custom components
 */
import { Injectable, ViewContainerRef } from '@angular/core';

// Import custom overrides (bar-chart, pie-chart, bubble-chart with custom styling)
// import { BarChartComponent } from '../chart-components/bar-chart/bar-chart.component';
// import { PieChartComponent } from '../chart-components/pie-chart/pie-chart.component';
// import { BubbleChartComponent } from '../chart-components/bubble-chart/bubble-chart.component';

// Import remaining components from shared library
import { BarChartComponent } from '@shared-lib/chart-components/bar-chart/bar-chart.component';
import { PieChartComponent } from '@shared-lib/chart-components/pie-chart/pie-chart.component';
import { BubbleChartComponent } from '@shared-lib/chart-components/bubble-chart/bubble-chart.component';
import { LineChartComponent } from '@shared-lib/chart-components/line-chart/line-chart.component';
import { CountComponent } from '@shared-lib/chart-components/step-line-chart/count.component';
import { StackedAreaChartComponent } from '@shared-lib/chart-components/stacked-area-chart/stacked-area-chart.component';
import { WaterfallChartComponent } from '@shared-lib/chart-components/waterfall-chart/waterfall-chart.component';
import { QuadBubbleChartComponent } from '@shared-lib/chart-components/quad-bubble-chart/quad-bubble-chart.component';
import { DrillDownComponent } from '@shared-lib/chart-components/drill-down/drill-down.component';
import { HorizontalBarChartComponent } from '@shared-lib/chart-components/horizontal-bar-chart/horizontal-bar-chart.component';
import { TreemapChartComponent } from '@shared-lib/chart-components/treemap-chart/treemap-chart.component';
import { LineColumnChartComponent } from '@shared-lib/chart-components/line-column-chart/line-column-chart.component';
import { CombinedChartComponent } from '@shared-lib/chart-components/combined-chart/combined-chart.component';
import { MatSliderComponent } from '@shared-lib/global-components/mat-slider/mat-slider.component';
import { WaterfallStackChartComponent } from '@shared-lib/chart-components/waterfall-stack-chart/waterfall-stack-chart.component';
import { InputBoxComponent } from '@shared-lib/global-components/input-box/input-box.component';
import { TableComponent } from '@shared-lib/global-components/table/table.component';
// import { ConfigurableFormComponent } from '@shared-lib/global-components/configurable-form/configurable-form.component';
import { EditableTableComponent } from '@shared-lib/chart-components/editable-table/editable-table.component';
import { DynamicEditTableComponent } from '@shared-lib/chart-components/dynamic-edit-table/dynamic-edit-table.component';
import { UploadFileComponent } from '@shared-lib/chart-components/upload-file/upload-file.component';
import { NumberStatsComponent } from '../../projects/my-app/src/app/number-stats/number-stats.component';
import { StackBarChartComponent } from '@shared-lib/chart-components/stack-bar-chart/stack-bar-chart.component';
import { GenericStackbarChart } from '@shared-lib/chart-components/generic-stackbar-chart/generic-stackbar-chart';
import { GenericTableComponent } from '@shared-lib/chart-components/generic-table/generic-table';
import { TreeDrillDownComponent } from '@shared-lib/chart-components/tree-drill-down/tree-drill-down.component';
import { DonutChartComponent } from '@shared-lib/chart-components/donut-chart/donut-chart.component';
import { MixedStackbarChartComponent } from '@shared-lib/chart-components/mixed-stackbar-chart/mixed-stackbar-chart.component';
// import { StackedHorizontalBarChartComponent } from '@shared-lib/chart-components/stacked-horizontal-bar-chart/stacked-horizontal-bar-chart.component';
// import { MultiLevelComparisonChartComponent } from '@shared-lib/chart-components/multi-level-comparison-chart/multi-level-comparison-chart.component';
import { ClusteredBarComponent } from '@shared-lib/chart-components/clustered-bar-chart/clustered-bar-chart.component';
import { BarLine } from '@shared-lib/chart-components/bar-line/bar-line';
import { MbarMline } from '@shared-lib/chart-components/mbar-mline/mbar-mline';
import { Notes } from '@shared-lib/global-components/notes/notes';
import { GanttChartComponent } from '@shared-lib/chart-components/gantt-chart/gantt-chart.component';
import { EntityManagementComponent } from '@shared-lib/chart-components/entity-management/entity-management.component';
import { SideEntityFormComponent } from '@shared-lib/chart-components/side-entity-form/side-entity-form.component';
import { StackedLineChartComponent } from '@shared-lib/chart-components/stacked-line-chart/stacked-line-chart.component';
// import { HorizontalBarChartSegmentComponent } from '@shared-lib/chart-components/horizontal-bar-chart-segment/horizontal-bar-chart-segment.component';
// import { ConfigurableMultiSeriesScatterChartComponent } from '@shared-lib/chart-components/configurable-multi-series-scatter-chart/configurable-multi-series-scatter-chart.component';
// import { RangeBidirectionalSliderComponent } from '@shared-lib/chart-components/range-bidirectional-slider/range-bidirectional-slider.component';
// import { MultiSeriesBarLineComboChartComponent } from '@shared-lib/chart-components/multi-series-bar-line-combo-chart/multi-series-bar-line-combo-chart.component';
// import { ConfigDrivenSuperTableComponent } from '@shared-lib/chart-components/config-driven-super-table/config-driven-super-table.component';
// import { MineAssessmentDrilldownDashboardComponent } from '@shared-lib/chart-components/mine-assessment-drilldown-dashboard/mine-assessment-drilldown-dashboard.component';
import { LandingModuleCardComponent } from '@shared-lib/global-components/landing-module-card/landing-module-card.component';
import { ImageShow } from '@shared-lib/global-components/image-show/image-show';

import { ButtonComponent } from '@shared-lib/global-components/button/button.component';
import { TextWidgetComponent } from '@shared-lib/global-components/text-widget/text-widget.component';
import { ChatComponent } from '@shared-lib/global-components/chat/chat.component';


@Injectable({
  providedIn: 'root'
})
export class ChartRendererService {

  renderChart(
    type: string,
    viewContainerRef: ViewContainerRef,
    chartData: any
  ): void {
    viewContainerRef.clear();

    let component: any;

    switch (type) {
      case 'count':
        component = CountComponent;
        break;
      case 'line-chart':
        component = LineChartComponent;
        break;
      case 'bar-chart':
        // CUSTOM: Uses custom BarChartComponent with pink/red color
        component = BarChartComponent;
        break;
      case 'pie-chart':
        // CUSTOM: Uses custom PieChartComponent with 70% radius
        component = PieChartComponent;
        break;
      // case 'mine-assessment-drilldown-dashboard':
      // case 'mine-assessment-dashboard':
      // case 'tree-driven-donut-analysis-chart':
      //   component = MineAssessmentDrilldownDashboardComponent;
      //   break;
      case 'number-stats':
        component = NumberStatsComponent;
        break;
      case 'bubble-chart':
        // CUSTOM: Uses custom BubbleChartComponent with pink color
        component = BubbleChartComponent;
        break;
      case 'stacked-area-chart':
        component = StackedAreaChartComponent;
        break;
      case 'waterfall-chart':
        component = WaterfallChartComponent;
        break;
      // case 'configurable-multi-series-scatter-chart':
      // case 'multi-series-scatter-chart':
      // case 'multi-series-scatter':
      // case 'Multi-series-scatter-chart':
      //   component = ConfigurableMultiSeriesScatterChartComponent;
      //       break;
      // case 'multi-series-bar-line-combo-chart':
      //   component = MultiSeriesBarLineComboChartComponent;
      //       break;
      // case 'config-driven-super-table':
      //   component = ConfigDrivenSuperTableComponent;
      //       break;
      case 'quad-bubble-chart':
        component = QuadBubbleChartComponent;
        break;
      case 'drill-down':
        component = DrillDownComponent;
        break;
      case 'stacked-line-chart':
        component = StackedLineChartComponent;
        break;
      // case 'range-bidirectional-slider':
      //   component = RangeBidirectionalSliderComponent;
        break;
      case 'stacked-line':
        component = StackedLineChartComponent;
        break;
      case 'horizontal-bar-chart':
        component = HorizontalBarChartComponent;
        break;
      case 'treemap-chart':
        component = TreemapChartComponent;
        break;
      case 'line-column-chart':
        component = LineColumnChartComponent;
        break;
      case 'slider':
        component = MatSliderComponent;
        break;
      case 'combined-chart':
        component = CombinedChartComponent;
        break;
      case 'waterfall-stack':
        component = WaterfallStackChartComponent;
        break;
      case 'input-box':
        component = InputBoxComponent;
        break;
      // case 'configurable-form':
      //   component = ConfigurableFormComponent;
      //   break;
      case 'editable-table':
        component = EditableTableComponent;
        break;
      case 'table':
        component = TableComponent;
        break;
      case 'stack-bar-chart':
        component = StackBarChartComponent;
        break;
      case 'dynamic-edit-table':
        component = DynamicEditTableComponent;
        break;
      case 'upload-file':
        component = UploadFileComponent;
        break;
      // Generic components (use utilities for all functionality)
      case 'generic-stack-bar-chart':
      case 'stack-bar-chart-generic':
        component = GenericStackbarChart;
        break;
      case 'generic-table':
      case 'table-generic':
        component = GenericTableComponent;

        break;
      case 'tree-drill-down':
        component = TreeDrillDownComponent;
        break;
      case 'donut-chart':
        component = DonutChartComponent;
        break;

      case 'mixed-stackbar-chart':
        component = MixedStackbarChartComponent;
        break;
      // case 'stacked-horizontal-bar-chart':
      //   component = StackedHorizontalBarChartComponent;
      //   break;
      // case 'multi-level-comparison-chart':
      //   component = MultiLevelComparisonChartComponent;
      //   break;
      case 'clustered-bar-chart':
        component = ClusteredBarComponent;
        break;
      case 'bar-line':
        component = BarLine;
        break;
      case 'mbar-mline':
        component = MbarMline;
        break;
      case 'notes':
        component = Notes;
        break;
      case 'gantt-chart':
        component = GanttChartComponent;
        break;
      case 'entity-table':
        component = EntityManagementComponent;
        break;
      case 'entity-form':
        component = SideEntityFormComponent;
        break;

      // case 'horizontal-bar-chart-segment':
      //   component = HorizontalBarChartSegmentComponent;
      //   break;
      case 'landing-module-card':
        component = LandingModuleCardComponent;
        break;  
      case 'button':
        component = ButtonComponent;
        break;
      case 'text':
      case 'text-widget':
      case 'label':
        component = TextWidgetComponent;
        break;
      case 'static-screen':
                      component = ImageShow;
                      break;  
      case 'chat':
                      component = ChatComponent;
                      break;                

      default:


        console.error(`Unknown chart type: ${type}`);
        return;
    }

    const componentRef = viewContainerRef.createComponent(component);

    if (componentRef.instance) {
      (componentRef.instance as any).data = chartData;
    }
  }
}
